import React, { useEffect } from "react"
import {
	Document,
	Page,
	Text,
	StyleSheet,
	PDFViewer,
	Font,
	usePDF,
} from "@react-pdf/renderer"
import { ReportSummaryPDF } from "./_pdfPrepare/ReportSummaryPDF"
import { ReportHeaderPDF } from "./_pdfPrepare/ReportHeaderPDF"
import { ReportSummaryTablePDF } from "./_pdfPrepare/ReportSummaryTablePDF"
import { ReportDrivePDF } from "./_pdfPrepare/ReportDrivePDF"
import { ReportFooter } from "./_pdfPrepare/ReportFooter"
import { mi2, ns, num, num2, usd2, usd3 } from "../../../../utils/formaters"
import { generatePDFReport } from "./_pdfPrepare/generatePDFReport"
import { RATES } from "@/constants/rates"
import { OnboardingGeneratingScreen } from "@/components/screens/onboarding/GeneratingScreen"
import { useLoadedReportContext } from "../_context/ReportContext"
import { useAtom, useAtomValue } from "jotai"
import { tripsAtom } from "@/state/trips"
import { reportFormDraftAtom } from "@/state/report"
import { useUploadPdf } from "@/queries/useReport"
import { Navigate, useParams } from "@/router"
import { useAnalytics } from "@/hooks/use-analytics"
import { PlainDate, PlainDateTime } from "@/utils/date"

Font.register({
	family: "Roboto",
	fonts: [
		{
			src: `https://fonts.cdnfonts.com/s/12165/Roboto-Regular.woff`,
		},
		{
			src: `https://fonts.cdnfonts.com/s/12165/Roboto-Bold.woff`,
			fontWeight: "bold",
		},
		{
			src: `https://fonts.cdnfonts.com/s/12165/Roboto-Medium.woff`,
			fontWeight: "semibold",
		},
		{
			src: `https://fonts.cdnfonts.com/s/12165/Roboto-Light.woff`,
			fontWeight: "light",
		},
	],
})

const styles = StyleSheet.create({
	viewer: {
		width: "100%",
		height: "100vh",
	},
	page: {
		padding: 30,
		paddingBottom: 15,
		flexDirection: "column",
		backgroundColor: "white",
		fontFamily: "Roboto",
	},
	vehicleTitle: {
		fontSize: 12,
		fontWeight: "bold",
		marginTop: 24,
		marginBottom: 16,
		marginLeft: 16,
	},
})

type ReportVehicle = {
	vehicle: string
	odometer?: ns
	business: ns
	commute: ns
	personal: ns
	totalDistance: ns
	businessValue: ns
	parking: ns
	tolls: ns
	totalBusinessValue: ns
	medical: ns
	moving: ns
	charity: ns
}

export type ReportTripPDF = {
	date: PlainDate
	timeStart: number
	why: string
	start: string
	end: string
	notes: string
	rate: ns
	distance: ns
	value: ns
	parking: ns
	tolls: ns
	total: ns
}

type ReportTripTotal = Omit<
	ReportTripPDF,
	"date" | "timeStart" | "why" | "start" | "end" | "notes" | "rate"
>

export type ReportProps = {
	businessRateRange: ns[]
	dateRange: [PlainDate, PlainDate]
	summary: {
		totalValue: ns
		businessValue: ns
		personalValue: ns
		parking: ns
		tolls: ns
		totalDistance: ns
		totalDrives: ns
	}
	vehicles: ReportVehicle[]
	vehiclesTotal: ReportVehicle
	trips: {
		vehicle: string
		pages: {
			trips: ReportTripPDF[]
			total: ReportTripTotal
		}[]
	}[]
	tripsTotal: ReportTripTotal
}

export const ReportPDFDocument: React.FC<ReportProps> = (props) => {
	const businessRateText = props.businessRateRange
		.map((x, i) => `${props.dateRange[i].year()} - ${usd3(x)}/mi`)
		.join(", ")

	return (
		<Document>
			<Page size="A4" orientation="landscape" style={styles.page}>
				<ReportHeaderPDF
					dateRange={PlainDate.formatDateRange(
						props.dateRange[0],
						props.dateRange[1]
					)}
					businessRate={businessRateText}
				/>

				<Text style={styles.vehicleTitle}>Report Summary</Text>
				<ReportSummaryPDF
					summary={{
						businessValue: usd2(props.summary.businessValue),
						totalDistance: mi2(props.summary.totalDistance),
						totalDrives: num(props.summary.totalDrives),
					}}
				/>

				<Text style={styles.vehicleTitle}>Vehicle Summary</Text>

				<ReportSummaryTablePDF
					footerData={{
						business: mi2(props.vehiclesTotal.business),
						businessValue: usd2(props.vehiclesTotal.businessValue),
					}}
					tableData={props.vehicles.map((x) => ({
						rate: props.businessRateRange.map((x) => usd3(x)).join(" - "),
						businessValue: usd2(x.businessValue),
						odometer: num2(x.odometer || "-"),
						business: mi2(x.business),
						vehicle: x.vehicle,
					}))}
				/>
				<ReportFooter />
			</Page>
			{props.trips.map((vehicle) => {
				return vehicle.pages.map((page, i) => {
					return (
						<Page
							key={i}
							size="A4"
							orientation="landscape"
							style={styles.page}
						>
							<ReportHeaderPDF
								dateRange={PlainDate.formatDateRange(
									props.dateRange[0],
									props.dateRange[1]
								)}
								businessRate={businessRateText}
							/>
							<Text style={styles.vehicleTitle}>
								Trips log: {vehicle.vehicle}
							</Text>
							<ReportDrivePDF
								rows={page.trips.map((t) => ({
									when: `${t.date.formatWeekdayDate()}\n${PlainDateTime.fromMinutes(
										t.date,
										t.timeStart
									).formatTime()}`,
									why: t.why,
									start: t.start,
									end: t.end,
									notes: t.notes,
									rate: usd3(t.rate),
									distance: mi2(t.distance),
									value: usd2(t.value),
									parking: usd2(t.parking),
									tolls: usd2(t.tolls),
									total: usd2(t.total),
								}))}
								pageTotal={{
									distance: mi2(page.total.distance),
									value: usd2(page.total.value),
									parking: usd2(page.total.parking),
									tolls: usd2(page.total.tolls),
									total: usd2(page.total.value),
								}}
								reportTotal={{
									distance: mi2(props.tripsTotal.distance),
									value: usd2(props.tripsTotal.value),
									parking: usd2(props.tripsTotal.parking),
									tolls: usd2(props.tripsTotal.tolls),
									total: usd2(props.tripsTotal.value),
								}}
							/>
							<ReportFooter />
						</Page>
					)
				})
			})}
		</Document>
	)
}

export const ReportPDFViewer: React.FC<ReportProps> = (props) => {
	return (
		<PDFViewer style={styles.viewer}>
			<ReportPDFDocument {...props} />
		</PDFViewer>
	)
}

export default function ReportPDF() {
	const analytics = useAnalytics("Report_Pdf")
	const [trips, setTrips] = useAtom(tripsAtom)
	const form = useAtomValue(reportFormDraftAtom)
	const report = useLoadedReportContext()
	const { mutateAsync: uploadPdf } = useUploadPdf()
	const { id } = useParams("/report/:id/pdf")

	React.useEffect(() => {
		if (report.report.pdf_url) {
			analytics.sendEvent("PDFReportPage__View")
		}
	}, [report.report.pdf_url])
	if (!report.report.paid) {
		console.error("Report not paid")
		return <Navigate to="/report/:id" params={{ id }} />
	}
	if (report.report.pdf_url) {
		return (
			<embed
				src={report.report.pdf_url}
				className="w-full h-screen"
				type="application/pdf"
			/>
		)
	}

	let props = generatePDFReport(
		RATES,
		[PlainDate.from(form.period.range[0]), PlainDate.from(form.period.range[1])],
		trips || [],
		form.vehicle
			? `${form.vehicle.make} ${form.vehicle.model} ${form.vehicle.year}`
			: "Default"
	)

	return (
		<PDFLoader
			props={props}
			onPdfReady={(blob) => {
				uploadPdf({ blob: blob, filename: "report.pdf" })
			}}
		/>
	)
}

type PDFLoaderProps = {
	props: ReportProps
	onPdfReady: (blob: Blob) => void
}
const PDFLoader: React.FC<PDFLoaderProps> = ({ props, onPdfReady }) => {
	const [instance, updateInstance] = usePDF({
		document: <ReportPDFDocument {...props} />,
	})

	useEffect(() => {
		if (instance.url && instance.blob) {
			onPdfReady(instance.blob)
		}
	}, [instance.url, instance.blob])

	if (instance.loading)
		return (
			<OnboardingGeneratingScreen onNext={() => {}} fixedStatus="Generating PDF" />
		)

	if (instance.error)
		return (
			<div className="p-8 text-center text-destructive">
				<div className="mb-2">⚠️ Error generating PDF</div>
				<div className="text-sm opacity-75">{instance.error}</div>
			</div>
		)

	return (
		<embed
			src={instance.url || "#"}
			className="w-full h-screen"
			type="application/pdf"
		/>
	)
}
